import axios from 'axios';
import store from '@/store';
import { getDevMockResponse } from '@/dev/mockApi';

const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL || 'http://localhost:5001',
});

// Dev bypass: serve mock data instead of calling the API
instance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (store.getters['auth/isDevBypass']) {
      const mock = getDevMockResponse(config.url, config.method, config);
      if (mock !== null) {
        config.adapter = (c) =>
          Promise.resolve({
            data: mock,
            status: 200,
            statusText: 'OK',
            headers: {},
            config: c,
          });
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Capture X-Request-ID from backend for support correlation (see LOGGING_STRATEGY)
function attachRequestId(error) {
  const id = error.response?.headers?.['x-request-id'] || error.response?.headers?.['X-Request-ID'];
  if (id) {
    error.requestId = id;
  }
  return error;
}

// Handle 401: try refresh once, retry request; otherwise logout and redirect (skip when dev bypass)
instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    attachRequestId(error);
    if (store.getters['auth/isDevBypass']) return Promise.reject(error);

    const status = error.response?.status;
    const config = error.config;

    if (status === 401) {
      const canRetry = config && !config.__retriedAfterRefresh && !config.url?.includes('/auth/refresh');
      if (canRetry) {
        config.__retriedAfterRefresh = true;
        try {
          await store.dispatch('auth/refreshToken');
          return instance(config);
        } catch (_) {
          // refresh failed, fall through to logout
        }
      }
      store.dispatch('auth/logout');
      window.location.href = '/login';
    }

    return Promise.reject(error);
  }
);

/**
 * Format an API error message for display. Appends request ID when present so support can search backend logs.
 * @param {Error} error - Axios error (with optional .requestId from backend X-Request-ID)
 * @param {string} fallback - Fallback message if error has no message
 * @returns {string}
 */
export function formatApiErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const message = error?.response?.data?.message || error?.message || fallback;
  const requestId = error?.requestId;
  if (requestId) {
    return `${message} If you contact support, please provide reference: ${requestId}`;
  }
  return message;
}

export default instance;
